import {
  AnchoredPanel,
  AnchoredPanelType,
  DateTimeDisplay,
  LabelMetaItem,
  MetaItemGroupsLayout,
  stateT,
} from "o4a-react";
import * as React from "react";
import { mergeStyleSets, Stack, Text } from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { emptyChar, metaItemLabelsGap } from "../../constants/uiConstants";
import {
  ContactList,
  IncidentProblemTypeEnum,
  SubscriptionInformation,
  Ticket,
} from "../../gen/clients/mchub-azure-api-client";
import { getIncidentProblemType, getIncidentSeverity, incidentStatusDetailsT } from "../../helpers/resourceHelper";
import { useSubscriptions } from "../../hooks/useSubscriptions";

export interface SupportViewAllValues {
  contactList: ContactList | undefined,
  ticket: Ticket | undefined;
  problemType: IncidentProblemTypeEnum | undefined;
  subscriptionInformation: SubscriptionInformation | undefined;
}

export interface SupportViewAllPanelProps {
  targetId: string;
  title: string;
  supportViewAllValues: SupportViewAllValues;
  onClose: () => void;
}

const classNames = mergeStyleSets({
  contactTextContainer: {
    display: "flex",
    fontSize: "13px",
    fontWeight: 400,
    paddingTop: "5px",
  },
  fieldTextContainer: { paddingTop: "12px" },
  metaSectionContainer: { marginTop: "32px" },
  metaSectionTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "rgb(50, 49, 48)",
    paddingBottom: "10px",
  },
});

export const SupportViewAllPanel = (
  { targetId, title, supportViewAllValues, onClose }: SupportViewAllPanelProps,
): JSX.Element => {
  const { subscriptions } = useSubscriptions();

  const getContactNames = (): JSX.Element => {
    const contactNames: JSX.Element[] = [];
    supportViewAllValues.contactList?.contactList.forEach(contact => {
      if (contact.contactName) {
        contactNames.push(
          <Text className={classNames.contactTextContainer} key={contact.contactName}>
            {contact.contactName}
          </Text>,
        );
      }
    });
    return (<div>{contactNames}</div>);
  };

  // const getContactPhones = (): JSX.Element => {
  //   const contactPhones: JSX.Element[] = [];
  //   incident?.contactList?.contactList?.forEach(contact => {
  //     if (contact.contactPhone) {
  //       contactPhones.push(
  //         <Text className={classNames.contactTextContainer} key={contact.contactPhone}>
  //           {contact.contactPhone}
  //         </Text>,
  //       );
  //     }
  //   });
  //   return (<div>{contactPhones}</div>);
  // };

  const getContactEmails = (): JSX.Element => {
    const contactEmails: JSX.Element[] = [];
    supportViewAllValues.contactList?.contactList?.forEach(contact => {
      if (contact.contactEmail) {
        contactEmails.push(
          <Text className={classNames.contactTextContainer} key={contact.contactEmail}>
            {contact.contactEmail}
          </Text>,
        );
      }
    });
    return (<div>{contactEmails}</div>);
  };

  // const getContactMeta = (): JSX.Element => {
  //   const contactElements: JSX.Element[] = [];
  //   incident?.contactList?.contactList?.forEach(contact => {
  //     if (contact.contactPhone) {
  //       contactElements.push(
  //         <Text className={classNames.contactTextContainer} key={contact.contactPhone}>
  //           {Messages.labels.personaPhone(contact.contactPhone || "")}
  //         </Text>,
  //       );
  //     }
  //     if (contact.contactEmail) {
  //       contactElements.push(
  //         <Text className={classNames.contactTextContainer} key={contact.contactEmail}>
  //           {Messages.labels.personaEmail(contact.contactEmail || "")}
  //         </Text>,
  //       );
  //     }
  //   });
  //   return (<div>{contactElements}</div>);
  // };

  const overviewMetaItemGroup: JSX.Element[] = [
    (
      <Stack tokens={{ childrenGap: metaItemLabelsGap }}>
        <LabelMetaItem label={Messages.labels.severity()} hideSeparator>
          {supportViewAllValues.ticket?.severity
            ? getIncidentSeverity(supportViewAllValues.ticket.severity) : ""}
        </LabelMetaItem>
        <LabelMetaItem label={Messages.labels.status()} hideSeparator>
          <>
            {(supportViewAllValues.ticket?.lifecycleState === "CLOSED"
              || !supportViewAllValues.ticket?.lifecycleDetails)
              && (stateT(supportViewAllValues.ticket?.lifecycleState || ""))}
            {supportViewAllValues.ticket?.lifecycleState !== "CLOSED"
              && supportViewAllValues.ticket?.lifecycleDetails
              && (
                incidentStatusDetailsT(supportViewAllValues.ticket?.lifecycleDetails)
              )}
          </>
        </LabelMetaItem>
        <LabelMetaItem label={Messages.labels.supportRequestId()} hideSeparator>
          {supportViewAllValues.ticket?.ticketNumber || ""}
        </LabelMetaItem>
        <LabelMetaItem label={Messages.labels.createdOn()} hideSeparator>
          <DateTimeDisplay date={supportViewAllValues.ticket?.timeCreated} />
        </LabelMetaItem>
        {/*       <LabelMetaItem label={Messages.labels.contactMethod()} hideSeparator>
          <Text>{getContactMeta()}</Text>
        </LabelMetaItem> */}
        <LabelMetaItem label={Messages.labels.summary()} hideSeparator>
          {supportViewAllValues.ticket?.title || ""}
        </LabelMetaItem>
        <LabelMetaItem label={Messages.labels.description()} hideSeparator>
          {supportViewAllValues.ticket?.description || ""}
        </LabelMetaItem>
      </Stack>
    ),
  ];

  const detailsMetaItemGroup: JSX.Element[] = [
    (
      <Stack tokens={{ childrenGap: metaItemLabelsGap }}>
        <LabelMetaItem label={Messages.labels.subscription()} hideSeparator>
          {subscriptions?.find(
            sub => sub.id === supportViewAllValues.subscriptionInformation?.subscriptionId,
          )?.name || ""}
        </LabelMetaItem>
        <LabelMetaItem label={Messages.labels.problemType()} hideSeparator>
          {supportViewAllValues.problemType
            ? getIncidentProblemType(supportViewAllValues.problemType) : emptyChar}
        </LabelMetaItem>
      </Stack>
    ),
  ];
  const contactInfoMetaItemGroup: JSX.Element[] = [
    (
      <Stack tokens={{ childrenGap: metaItemLabelsGap }}>
        <LabelMetaItem label={Messages.labels.name()} hideSeparator>
          {getContactNames()}
        </LabelMetaItem>
        <LabelMetaItem label={Messages.labels.email()} hideSeparator>
          {getContactEmails()}
        </LabelMetaItem>
        {/*         <LabelMetaItem label={Messages.labels.phone()} hideSeparator>
          {getContactPhones()}
        </LabelMetaItem> */}
      </Stack>
    ),
  ];
  return (
    <AnchoredPanel
      title={title}
      onClose={onClose}
      isOpen
      anchorId={targetId}
      type={AnchoredPanelType.MEDIUM}
    >
      <>
        <div className={classNames.metaSectionContainer}>
          <div className={classNames.metaSectionTitle}>
            {Messages.detailSupport.overview.titles.overview()}
          </div>
          <MetaItemGroupsLayout>
            {overviewMetaItemGroup}
          </MetaItemGroupsLayout>
        </div>
        <div className={classNames.metaSectionContainer}>
          <div className={classNames.metaSectionTitle}>
            {Messages.detailSupport.overview.titles.details()}
          </div>
          <MetaItemGroupsLayout>
            {detailsMetaItemGroup}
          </MetaItemGroupsLayout>
        </div>
        {supportViewAllValues.contactList?.contactList
          && supportViewAllValues.contactList.contactList.length > 0
          && (
            <div className={classNames.metaSectionContainer}>
              <div className={classNames.metaSectionTitle}>
                {Messages.detailSupport.overview.titles.contactInfo()}
              </div>
              <MetaItemGroupsLayout>
                {contactInfoMetaItemGroup}
              </MetaItemGroupsLayout>
            </div>
          )}
      </>
    </AnchoredPanel>
  );
};
